import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import { useMe } from '@/lib/auth'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { CopyButton } from './copy-button'

/**
 * First-run: create the one wallet, show its recovery phrase once, and
 * require an acknowledgement before anything else loads (same gate as the
 * mobile app's onboarding).
 */
export function WalletSetup() {
  const trpc = useTRPC()
  const { refetch } = useMe()
  const [seedPhrase, setSeedPhrase] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const create = useMutation(
    trpc.wallet.create.mutationOptions({
      onSuccess: (data) => setSeedPhrase(data.seedPhrase),
      onError: (err) => setError(errorMessage(err)),
    }),
  )
  const acknowledge = useMutation(
    trpc.wallet.acknowledgeRecoveryPhrase.mutationOptions({
      onSuccess: () => void refetch(),
      onError: (err) => setError(errorMessage(err)),
    }),
  )
  const words = seedPhrase?.split(/\s+/) ?? []

  return (
    <div className="card mx-auto max-w-xl p-8">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-control bg-accent-soft text-accent">
          <ShieldCheck className="size-5" />
        </span>
        <div>
          <h2 className="text-lg font-semibold">Set up your trading wallet</h2>
          <p className="text-sm text-muted">One Solana wallet, created and held for you.</p>
        </div>
      </div>
      {seedPhrase === null ? (
        <>
          <p className="text-sm text-muted">
            We generate a recovery phrase and show it once. Write it down somewhere safe: it is the only way to
            restore this wallet outside Dispence.
          </p>
          <button type="button" className="btn btn-primary mt-6" disabled={create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Creating…' : 'Create wallet'}
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-muted">Your recovery phrase. Anyone with these words controls the wallet.</p>
          <ol className="mt-4 grid grid-cols-3 gap-2 font-mono text-sm">
            {words.map((word, i) => (
              <li key={i} className="control flex items-center gap-2 px-3 py-2">
                <span className="w-5 text-right text-xs text-muted">{i + 1}</span>
                <span>{word}</span>
              </li>
            ))}
          </ol>
          <div className="mt-4 flex items-center justify-between">
            <CopyButton value={seedPhrase} label="Copy phrase" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="accent-accent" />
              I saved my recovery phrase
            </label>
          </div>
          <button
            type="button"
            className="btn btn-primary mt-6 w-full"
            disabled={!saved || acknowledge.isPending}
            onClick={() => acknowledge.mutate()}
          >
            {acknowledge.isPending ? 'Finishing…' : 'Continue'}
          </button>
        </>
      )}
      {error ? <p className="mt-4 text-sm text-negative">{error}</p> : null}
    </div>
  )
}
